import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Alert,
  Checkbox,
} from "@mui/material";
import {
  FaRocket,
  FaFileExcel,
  FaEye,
  FaUpload,
  FaUsers,
  FaFileAlt,
  FaCheckCircle,
  FaEdit,
  FaTrash,
  FaPhone,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, getHeader, toastError } from "../../utils/util";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import AutoSelectTemplate from "../../widgets/AutoSelectTemplate";
import SectionLabel from "../../widgets/SectionLabel";
import RcsTemplateById from "./RcsTemplateById";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import ContactsTabs from "./ContactsTabs";
import { ca } from "date-fns/locale";

const StyledCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  marginBottom: theme.spacing(3),
}));

const StepIcon = styled(Box)(({ theme, active }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: active ? "#667eea" : "white",
  fontWeight: "bold",
}));

const UploadZone = styled(Paper)(({ theme, isdragged }) => ({
  border: `2px dashed ${
    isdragged ? theme.palette.primary.main : theme.palette.grey[300]
  }`,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  backgroundColor: isdragged
    ? theme.palette.action.hover
    : theme.palette.background.paper,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(45deg, #f5f7fa 0%, #c3cfe2 100%)",
  minHeight: 400,
  display: "flex",
  flexDirection: "column",
}));

const RcsCampaignLauncher = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/rcs/get-approved-templates`
  );
  const [activeStep, setActiveStep] = useState(0);
  const [campaignData, setCampaignData] = useState({
    campaignName: "",
    template: "",
    message: "",
    contacts: [],
    uploadedFile: null,
    templateContent: null,
  });
  const [isDragged, setIsDragged] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  // Sample data
  const templates = data ?? [];

  const steps = ["Select Template", "Upload Contacts", "Preview & Launch"];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleTemplateSelect = (templateId) => {
    const selectedTemplate = templates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      const content =
        selectedTemplate.data.content.richCardDetails.standalone.content;
      setCampaignData((prev) => ({
        ...prev,
        template: templateId,
        templateContent: { ...content },
        message: content.cardDescription,
      }));
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([["phoneNumber", "name"]]);
    XLSX.utils.book_append_sheet(wb, ws, "Contacts");
    XLSX.writeFile(wb, "contacts_template.xlsx");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setCampaignData((prev) => ({ ...prev, uploadedFile: file }));
      setUploadProgress(0);
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const contacts = parsedData
          .slice(1)
          .map((row, index) => ({
            id: index + 1,
            phone: String(row[0] || ""),
            name: row[1] ? String(row[1]) : "Unknown",
            selected: true,
          }))
          .filter((contact) => contact.phone);
        setCampaignData((prev) => ({ ...prev, contacts }));
        setUploadProgress(100);
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragged(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragged(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragged(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload({ target: { files: [files[0]] } });
    }
  };

  const removeContact = (contactId) => {
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.filter((c) => c.id !== contactId),
    }));
  };

  const toggleSelect = (id) => {
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) =>
        c.id === id ? { ...c, selected: !c.selected } : c
      ),
    }));
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setCampaignData((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => ({ ...c, selected: checked })),
    }));
  };

  const launchCampaign = async () => {
    if (!campaignData.campaignName) {
      toast.error("Campaign name is required!");
      return;
    }
    if (!campaignData.template) {
      toast.error("Campaign name is required!");
      return;
    }
    if (!campaignData.selectedGroup && campaignData.contacts.length < 1) {
      toast.error("Contacts List is required!");
      return;
    }

    try {
      setSendingCampaign(true);
      const response = await axios.post(
        `${API_ENDPOINT}/rcs/launch-campaign`,
        {
          contacts: campaignData.contacts,
          selectedGroup: campaignData.selectedGroup,
          campaignName: campaignData.campaignName,
          startIndex: campaignData?.startIndex,
          endIndex: campaignData?.endIndex,
          templateId: campaignData.template.id,
        },
        getHeader()
      );
      toast.success(response.data.message);
      setTimeout(() => {
        navigate(`/home/queue-monitor?type=rcs`);
      }, 700);
    } catch (error) {
      toastError(error);
    } finally {
      setSendingCampaign(false);
    }

    return;
    // Reset form
    setActiveStep(0);
    setCampaignData({
      template: "",
      message: "",
      contacts: [],
      uploadedFile: null,
      templateContent: null,
    });
    setUploadProgress(0);
    setSearchQuery("");
  };
  const handleCampaignName = useCallback((e) => {
    const value = e.target.value;
    const regex = /^[A-Za-z0-9_ ]*$/; // allowed characters
    if (regex.test(value) && value.length <= 100) {
      setCampaignData((prev) => ({ ...prev, campaignName: value }));
    }
  }, []);
  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return (
      <Box mt={3}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Box>
    );
  }

  const renderTemplatePreview = (content) => {
    if (!content) return null;
    return (
      <Paper variant="outlined" sx={{ p: 3, bgcolor: "white", mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {content.cardTitle}
        </Typography>
        {content.cardMedia && (
          <img
            src={content.cardMedia.contentInfo.fileUrl}
            alt="Media"
            style={{ maxWidth: "100%", height: "auto", marginBottom: 16 }}
          />
        )}
        <Typography style={{ whiteSpace: "pre-wrap" }} gutterBottom>
          {content.cardDescription}
        </Typography>
        <Grid container spacing={1}>
          {content.suggestions.map((sug, index) => (
            <Grid item key={index}>
              <Button variant="contained">{sug.action.plainText}</Button>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  const selectedCount = campaignData.contacts.filter((c) => c.selected).length;
  const allSelected =
    selectedCount === campaignData.contacts.length &&
    campaignData.contacts.length > 0;
  const indeterminate = selectedCount > 0 && !allSelected;
  const filteredContacts = campaignData.contacts.filter((c) =>
    String(c.phone).includes(searchQuery)
  );

  console.log(campaignData);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              label="Campaign Name"
              type="text"
              size="small"
              sx={{ my: 2 }}
              fullWidth
              helperText="Only letters, space, underscore and numbers allowed (max 100 chars)"
              value={campaignData.campaignName}
              onChange={handleCampaignName}
            />
            <SectionLabel>Choose a template for your RCS campaign</SectionLabel>
            <Box my={2}>
              <AutoSelectTemplate
                label="Select Template"
                value={selectedTemplate}
                onChange={(newV) => {
                  console.log(newV);
                  setCampaignData((prev) => ({ ...prev, template: newV }));
                  setSelectedTemplate(newV);
                }}
              />
            </Box>

            {/* <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} md={6} key={template.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: campaignData.template === template.id ? 2 : 1,
                      borderColor:
                        campaignData.template === template.id
                          ? "primary.main"
                          : "divider",
                      transition: "all 0.3s ease",
                    }}
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <FaFileAlt size={24} color="#667eea" />
                        <Box>
                          <Typography variant="h6">{template.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {template.description}
                          </Typography>
                        </Box>
                        {campaignData.template === template.id && (
                          <FaCheckCircle color="#4CAF50" size={20} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid> */}
            {selectedTemplate && <RcsTemplateById id={selectedTemplate.id} />}
          </Box>
        );

      case 1:
        return (
          <ContactsTabs
            campaignData={campaignData}
            setCampaignData={setCampaignData}
            isDragged={isDragged}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleFileUpload={handleFileUpload}
            downloadTemplate={downloadTemplate}
            uploadProgress={uploadProgress}
            selectedCount={selectedCount}
            indeterminate={indeterminate}
            allSelected={allSelected}
            handleSelectAll={handleSelectAll}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filteredContacts={filteredContacts}
            toggleSelect={toggleSelect}
            removeContact={removeContact}
          />
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom color="textSecondary">
              Preview your campaign before launching
            </Typography>

            <PreviewCard>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    <FaPhone />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">RCS Message Preview</Typography>
                  </Box>
                </Box>

                {renderTemplatePreview(campaignData.templateContent)}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Campaign Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Campaign name:
                    </Typography>
                    <Typography variant="body1">
                      {campaignData.campaignName || "NA"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Template:
                    </Typography>
                    <Typography variant="body1">
                      {campaignData.template?.name || "Not selected"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Contacts:
                    </Typography>
                    <Typography variant="body1">
                      {campaignData.selectedGroup
                        ? campaignData.selectedGroup
                        : `${selectedCount} contacts`}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </PreviewCard>

            <Alert severity="info" sx={{ mt: 2 }}>
              Review all details before launching. This action cannot be undone.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", p: 3 }}>
      <StyledCard>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <FaRocket size={32} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Launch RCS Campaign
              </Typography>
              <Typography variant="subtitle1">
                Create and send rich communication services campaigns
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={() => (
                    <StepIcon active={index === activeStep}>
                      {index + 1}
                    </StepIcon>
                  )}
                >
                  <Typography variant="body2" sx={{ color: "white" }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </StyledCard>

      <Paper sx={{ p: 3, mt: 2 }}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<FaEdit />}
          >
            Back
          </Button>

          <Box>
            {activeStep === steps.length - 1 ? (
              <GlassButtonLoading
                variant="contained"
                loading={sendingCampaign}
                onClick={launchCampaign}
                startIcon={<FaRocket />}
                size="large"
              >
                Launch Campaign
              </GlassButtonLoading>
            ) : (
              <GlassButton
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 &&
                    (!campaignData.template || !campaignData.campaignName)) ||
                  (activeStep === 1 &&
                    selectedCount === 0 &&
                    !campaignData.selectedGroup)
                }
                endIcon={
                  activeStep === steps.length - 2 ? <FaEye /> : <FaUsers />
                }
              >
                {activeStep === steps.length - 2 ? "Preview" : "Next"}
              </GlassButton>
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Preview</DialogTitle>
        <DialogContent>{/* Preview content would go here */}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RcsCampaignLauncher;
